import { useEffect, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, gramas } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/ds";
import { ImageUpload } from "@/components/ds/ImageUpload";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

const norm = (v) =>
  (v || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const EMPTY = {
  brand: "",
  material: "PLA",
  color_name: "",
  manufacturer_color: "",
  supplier: "",
  min_stock_g: "0",
  initial_qty_g: "",
  paid_value: "",
  notes: "",
  photo_path: null,
};

export function DevFilamentModal({ projectId, linked = [], onClose, onSaved }) {
  const [tab, setTab] = useState("existing");
  const [filaments, setFilaments] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api
      .get("/filaments")
      .then(({ data }) => setFilaments(data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const link = async (filamentId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dev-projects/${projectId}/filaments`, { id: filamentId });
      onSaved(data);
      toast.success("Filamento vinculado ao projeto.");
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const createAndLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: fil } = await api.post("/filaments", {
        brand: form.brand,
        material: form.material,
        color_name: form.color_name,
        manufacturer_color: form.manufacturer_color,
        supplier: form.supplier,
        min_stock_g: Number(form.min_stock_g || 0),
        initial_qty_g: Number(form.initial_qty_g || 0),
        paid_value: form.paid_value === "" ? null : Number(form.paid_value),
        notes: form.notes,
        photo_path: form.photo_path,
        active: true,
      });
      const { data } = await api.post(`/dev-projects/${projectId}/filaments`, { id: fil.id });
      onSaved(data);
      toast.success("Filamento cadastrado no estoque e vinculado ao projeto.");
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const visible = filaments.filter((f) => {
    if (linked.includes(f.id)) return false;
    if (!search.trim()) return true;
    const q = norm(search);
    return norm(`${f.brand} ${f.material} ${f.color_name}`).includes(q);
  });

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Adicionar filamento"
      description="Escolha um filamento do estoque ou cadastre um novo — o registro é o mesmo usado pelo Estoque."
      testid="dev-filament-modal"
    >
      <div className="mt-2 flex gap-2">
        <button
          onClick={() => setTab("existing")}
          data-testid="filament-tab-existing"
          className={`tr-chip ${tab === "existing" ? "tr-chip-active" : ""}`}
        >
          Do estoque
        </button>
        <button
          onClick={() => setTab("new")}
          data-testid="filament-tab-new"
          className={`tr-chip ${tab === "new" ? "tr-chip-active" : ""}`}
        >
          Cadastrar novo
        </button>
      </div>

      {tab === "existing" ? (
        <div className="mt-4 space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar filamento..."
              className="pl-9"
              data-testid="dev-filament-search"
            />
          </div>
          <div className="max-h-[45vh] space-y-2 overflow-y-auto">
            {visible.length === 0 && (
              <p className="py-6 text-center text-xs text-muted-foreground">
                Nenhum filamento disponível. Cadastre um novo na aba ao lado.
              </p>
            )}
            {visible.map((f) => (
              <button
                key={f.id}
                disabled={busy}
                onClick={() => link(f.id)}
                data-testid={`pick-filament-${f.id}`}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5 text-left transition-colors hover:border-brand/40"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm">
                    {f.material} {f.color_name}
                  </p>
                  <p className="truncate text-[11px] text-muted-foreground">{f.brand}</p>
                </div>
                <span className="shrink-0 font-num text-xs text-muted-foreground">
                  {gramas(f.balance_g)}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <form onSubmit={createAndLink} className="mt-4 flex flex-col gap-4">
          <ImageUpload
            value={form.photo_path}
            onChange={(p) => setForm((f) => ({ ...f, photo_path: p }))}
            testid="dev-filament-image"
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Marca">
              <Input value={form.brand} onChange={set("brand")} required placeholder="Ex.: F3D" data-testid="dev-filament-brand" />
            </Field>
            <Field label="Material">
              <Input value={form.material} onChange={set("material")} required placeholder="Ex.: PLA" data-testid="dev-filament-material" />
            </Field>
            <Field label="Cor comercial">
              <Input value={form.color_name} onChange={set("color_name")} required placeholder="Ex.: Rosa Bebê" data-testid="dev-filament-color" />
            </Field>
            <Field label="Cor do fabricante">
              <Input value={form.manufacturer_color} onChange={set("manufacturer_color")} data-testid="dev-filament-manufacturer" />
            </Field>
            <Field label="Fornecedor">
              <Input value={form.supplier} onChange={set("supplier")} data-testid="dev-filament-supplier" />
            </Field>
            <Field label="Estoque mínimo (g)">
              <Input type="number" min="0" value={form.min_stock_g} onChange={set("min_stock_g")} className="font-num" data-testid="dev-filament-min" />
            </Field>
            <Field label="Quantidade inicial (g)">
              <Input type="number" min="0" value={form.initial_qty_g} onChange={set("initial_qty_g")} className="font-num" data-testid="dev-filament-qty" />
            </Field>
            <Field label="Valor pago (R$)">
              <Input type="number" min="0" step="0.01" value={form.paid_value} onChange={set("paid_value")} className="font-num" data-testid="dev-filament-paid" />
            </Field>
          </div>
          <Field label="Observações">
            <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="dev-filament-notes" />
          </Field>
          <Button
            type="submit"
            disabled={busy}
            data-testid="save-dev-filament-btn"
            className="h-12 gap-2 rounded-full font-semibold transition-transform active:scale-95"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Cadastrar e vincular
          </Button>
        </form>
      )}
    </ResponsiveModal>
  );
}
